import React, { useState, useMemo, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, deleteDoc, getDoc, getDocs, query, where, limit } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { getAllPhoneNumbers, getPrimaryPhone, normalizePhones, setPrimaryNumber } from '../utils/customerPhone';
import { reserveCustomerNumber } from '../utils/customerNumber';
import { propagateCustomerDetails, propagationFailureMessage } from '../utils/customerPropagation';
import CreditHoldWarning from '../components/CreditHoldWarning';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';
import { compareNames, nameMatchRank } from '../utils/nameSort';

// Digits-only view of a phone string, so a search for "410796" finds a number
// stored as "410-796-4747" regardless of how either side is punctuated.
const digits = (s) => (s || '').replace(/\D/g, '');

// Cheap existence check: read AT MOST 1 doc per collection (limit(1)), except
// quotes where we must exclude drafts client-side (drafts are never persisted to
// Firestore, so a where('isDraft','==',false) would wrongly exclude every doc —
// see CustomerProfile). Returns true if the customer has any FINALIZED quote,
// any invoice, or any payment. Throws on error so callers can fail safe (BLOCK).
async function customerHasLinkedDocs(customerId) {
  // invoices: any invoice blocks
  const invSnap = await getDocs(query(collection(db, 'invoices'), where('customerId', '==', customerId), limit(1)));
  if (!invSnap.empty) return true;
  // payments: any payment blocks
  const paySnap = await getDocs(query(collection(db, 'payments'), where('customerId', '==', customerId), limit(1)));
  if (!paySnap.empty) return true;
  // quotes: only NON-DRAFT quotes block (drafts ignored)
  const quoteSnap = await getDocs(query(collection(db, 'quotes'), where('customerId', '==', customerId)));
  if (quoteSnap.docs.some(d => !d.data().isDraft)) return true;
  return false;
}

function Customers({ user }) {
  const navigate = useNavigate();
  const { customers, loadingCustomers: loading, addCustomerToCache, updateCustomerInCache, removeCustomerFromCache } = useData();
  const { isAdmin } = useRole();
  // Credit hold is shown here, never changed here — placing and lifting live on
  // the customer profile, behind canManageCreditHold.
  const { theme, themeName } = useTheme();
  const isDark = themeName === 'dark';
  const userName = user?.email?.split('@')[0]?.toUpperCase();

  // Flat blue palette — overrides the dark theme vars. No logic depends on theme.
  const C_TEXT = '#03045E';
  const C_TEXT_2 = '#6B7C93';
  const C_TEXT_3 = '#6B7C93';
  const C_BORDER = '#E3ECF5';
  const C_BG_CARD = '#FFFFFF';
  const C_BG_CARD_SOLID = '#FFFFFF';
  const C_CYAN = '#0077B6';
  const C_CYAN_BRIGHT = '#0077B6';
  const C_VIOLET = '#0077B6';
  const C_VIOLET_BRIGHT = '#0077B6';
  const C_GREEN_BRIGHT = '#0F6E56';
  const C_RED_BRIGHT = '#A32D2D';
  const GRADIENT = '#0077B6';

  const cyanGlassButton = {
    background: '#E6F1FB',
    border: '1px solid #0077B6',
    color: '#0077B6',
    padding: '10px 18px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.3s, border-color 0.3s',
  };

  const neutralButton = {
    background: '#FFFFFF',
    border: `1px solid ${C_BORDER}`,
    color: C_TEXT,
    padding: '10px 18px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.3s, border-color 0.3s',
  };
  const neutralButtonHover = '#CDE7F5';
  const neutralButtonBg = neutralButton.background;

  const gradientButton = {
    background: '#0077B6',
    border: 'none',
    color: '#fff',
    padding: '12px 24px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.5px',
    boxShadow: '0 4px 14px rgba(0,119,182,0.3)',
    transition: 'transform 0.3s, box-shadow 0.3s',
  };

  const labelStyle = {
    color: C_TEXT_2,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 8,
    display: 'block',
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    background: '#FFFFFF',
    border: `1px solid ${C_BORDER}`,
    borderRadius: 10,
    color: C_TEXT,
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.3s, box-shadow 0.3s, background 0.3s',
    boxSizing: 'border-box',
  };

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(50);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [accountFilter, setAccountFilter] = useState('all');
  const [form, setForm] = useState({
    name: '', address: '', phone: '', email: '',
    taxExempt: false, notes: ''
  });

  // ── Delete customer (admin + password gated, reuses the app's re-auth flow) ──
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const initiateDelete = (customer) => {
    if (!isAdmin) { alert('Only admins can delete customers.'); return; }
    setCustomerToDelete(customer);
    setDeletePassword('');
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setCustomerToDelete(null);
    setDeletePassword('');
    setDeleteError('');
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;
    setDeleting(true);
    setDeleteError('');
    try {
      // 1. Password gate — reuse the app's re-authentication (own login password).
      const credential = EmailAuthProvider.credential(auth.currentUser.email, deletePassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
    } catch (err) {
      console.error('Reauth error:', err);
      setDeleteError(
        err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential'
          ? '❌ Incorrect password. Please try again.'
          : '❌ Error: ' + err.message
      );
      setDeleting(false);
      return;
    }

    // 2. Existence check — FAIL SAFE: any error blocks the delete.
    let hasLinks;
    try {
      hasLinks = await customerHasLinkedDocs(customerToDelete.id);
    } catch (err) {
      console.error('Existence check failed:', err);
      setDeleteError('❌ Could not verify customer history. Deletion blocked for safety.');
      setDeleting(false);
      return;
    }
    if (hasLinks) {
      setDeleteError('Cannot delete: this customer has linked invoices, quotes, or payments.');
      setDeleting(false);
      return;
    }

    // 3. Real delete + instant cache removal.
    try {
      await deleteDoc(doc(db, 'customers', customerToDelete.id));
      removeCustomerFromCache(customerToDelete.id);
      closeDeleteModal();
    } catch (err) {
      console.error('Delete error:', err);
      setDeleteError('❌ Error: ' + err.message);
    }
    setDeleting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Set only once an EDIT has been written: the customer record as saved.
    let savedEdit = null;
    try {
      if (editingCustomer) {
        // The single Phone box here edits the PRIMARY number. Writing only the
        // legacy `phone` string left a multi-phone customer's `phones` array
        // untouched, and the profile (which reads `phones` first) kept showing
        // the old number — then wrote it back over `phone` on its next save.
        // So both shapes are written together. `phones` comes from a fresh read,
        // not the cache, so secondary numbers can never be lost to a stale copy.
        const snap = await getDoc(doc(db, 'customers', editingCustomer.id));
        const phones = setPrimaryNumber(normalizePhones(snap.exists() ? snap.data() : editingCustomer), form.phone);
        const payload = { ...form, phones, phone: getPrimaryPhone({ phones }) };
        await updateDoc(doc(db, 'customers', editingCustomer.id), payload);
        // Patch the cache in place instead of a full ~9,000-read re-fetch.
        updateCustomerInCache(editingCustomer.id, payload);
        savedEdit = { id: editingCustomer.id, ...(snap.exists() ? snap.data() : editingCustomer), ...payload };
      } else {
        const newData = {
          ...form,
          phone: form.phone.trim(),
          customerNumber: await reserveCustomerNumber(customers),
          createdAt: new Date().toISOString(),
        };
        const ref = await addDoc(collection(db, 'customers'), newData);
        // Add to the cache immediately (prepends) — appears in the list instantly,
        // no full re-fetch.
        addCustomerToCache({ id: ref.id, ...newData });
      }
      setForm({ name: '', address: '', phone: '', email: '', taxExempt: false, notes: '' });
      setShowForm(false);
      setEditingCustomer(null);
    } catch (err) {
      console.error('Error saving customer:', err);
      alert('Error saving customer. Please try again.');
    }

    // The customer is saved. Now copy the details onto their documents — the
    // same shared helper the profile uses, run after and separately so a
    // failure here can never undo or block the save above.
    if (savedEdit) {
      const failure = propagationFailureMessage(await propagateCustomerDetails(savedEdit.id, savedEdit));
      if (failure) alert(failure);
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setForm({
      name: customer.name || '',
      address: customer.address || '',
      phone: getPrimaryPhone(customer),
      email: customer.email || '',
      taxExempt: !!customer.taxExempt,
      notes: customer.notes || ''
    });
    setShowForm(true);
  };

  const openNew = () => {
    setShowForm(true);
    setEditingCustomer(null);
    setForm({ name: '', address: '', phone: '', email: '', taxExempt: false, notes: '' });
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingCustomer(null);
  };

  // Debounce the search box so filtering doesn't run on every keystroke. The
  // input updates `search` immediately (stays responsive); `debouncedSearch` is
  // the value the filter actually uses.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 200);
    return () => clearTimeout(t);
  }, [search]);

  // New search/filter → start back at the top so we never render a huge list.
  useEffect(() => {
    setVisibleCount(50);
  }, [search, accountFilter]);

  // Filter over the FULL customers array, memoized so unrelated re-renders
  // (e.g. theme toggle) don't recompute it. Slicing for display happens after,
  // so searches always cover every record.
  const filteredCustomers = useMemo(() => {
    // Computed once per search term, not per customer.
    const searchDigits = digits(debouncedSearch);
    return customers.filter(c => {
      // Phone matches on DIGITS ONLY, across EVERY number the customer has (the
      // phones array plus the legacy field). Skipped entirely when the term has
      // no digits, so a name search behaves exactly as it did before.
      const matchPhone = searchDigits.length > 0 &&
        getAllPhoneNumbers(c).some(n => digits(n).includes(searchDigits));
      const matchSearch =
        c.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        matchPhone ||
        c.customerNumber?.toLowerCase().includes(debouncedSearch.toLowerCase());
      const matchType =
        accountFilter === 'all' ||
        (accountFilter === 'account' && c.isAccountCustomer) ||
        (accountFilter === 'regular' && !c.isAccountCustomer);
      return matchSearch && matchType;
    });
  }, [customers, debouncedSearch, accountFilter]);

  // Only show the results list once the user has actually searched or chosen a
  // non-default account filter — otherwise we'd dump all ~9,000 rows at once.
  const hasActiveQuery = debouncedSearch.trim() !== '' || accountFilter !== 'all';

  // Cap how many rows actually render; "Load More" raises this.
  // ── ORDER ─────────────────────────────────────────────────────────────
  // filteredCustomers above decides WHICH customers are shown. This only puts
  // them in order, so the count and the matches are exactly what they were.
  //
  // Before this, there was no sort here at all: the list kept the order the
  // customers were loaded in, which is customerNumber DESCENDING — newest
  // customer first — whether or not anything had been typed.
  //
  // NO SEARCH: A to Z by the full stored name, via the shared comparator.
  //
  // WITH A SEARCH: best match first, in four tiers —
  //   0  name starts with the query
  //   1  a word in the name starts with it
  //   2  it appears somewhere in the name
  //   3  matched on phone or customer number, not on name at all
  // and alphabetical within a tier, so equal matches do not shuffle between
  // renders. "smith" puts Smith Fencing above Anderson Smith.
  //
  // Whether there IS a search is decided with trim(), the same test
  // hasActiveQuery uses, so a box holding only spaces orders as "no search".
  const sortedCustomers = useMemo(() => {
    const q = debouncedSearch.trim();
    if (!q) {
      return [...filteredCustomers].sort((a, b) => compareNames(a.name, b.name));
    }
    // Rank computed once per customer, not once per comparison.
    return filteredCustomers
      .map(c => {
        const r = nameMatchRank(c.name, q);
        return { c, r: r === null ? 3 : r };
      })
      .sort((x, y) => (x.r - y.r) || compareNames(x.c.name, y.c.name))
      .map(x => x.c);
  }, [filteredCustomers, debouncedSearch]);

  const visibleCustomers = sortedCustomers.slice(0, visibleCount);

  const focusInputCyan = (e) => {
    e.target.style.borderColor = `${C_CYAN}80`;
    e.target.style.boxShadow = `0 0 0 3px ${C_CYAN}1A`;
  };
  const blurInputDefault = (e) => {
    e.target.style.borderColor = C_BORDER;
    e.target.style.boxShadow = 'none';
  };

  return (
    <>
      <Sidebar />
      <div style={{
        minHeight: '100vh',
        marginLeft: SIDEBAR_W,
        background: '#CDE7F5',
        padding: 24,
        color: C_TEXT,
        transition: 'background 0.3s ease, color 0.3s ease',
      }}>
      <style>{`
        select.dark-select option { background: #FFFFFF; color: #03045E; }
        .customer-row:hover { background: #E6F1FB !important; border-color: #E3ECF5 !important; }
      `}</style>

      <div style={{ maxWidth: 1152, margin: '0 auto' }}>

        {/* ── Header bar ── */}
        <div style={{
          background: C_BG_CARD,
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          border: `1px solid ${C_BORDER}`,
          borderRadius: 16,
          padding: '20px 24px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div>
              <h1 style={{
                fontSize: 22, fontWeight: 800,
                color: C_TEXT,
                margin: 0,
              }}>
                Customers
              </h1>
              <p style={{
                color: C_TEXT_2, fontSize: 12, margin: 0,
                letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600,
              }}>
                Customer Database
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {userName && (
              <span style={{ color: C_TEXT_2, fontSize: 13 }}>
                Salesperson: <span style={{ color: C_CYAN_BRIGHT, fontWeight: 700 }}>{userName}</span>
              </span>
            )}
            <button
              onClick={() => navigate('/import-customers')}
              style={cyanGlassButton}
              onMouseEnter={e => { e.currentTarget.style.background = `${C_CYAN}2E`; }}
              onMouseLeave={e => { e.currentTarget.style.background = `${C_CYAN}1A`; }}
            >
              📥 Import CSV
            </button>
          </div>
        </div>

        {/* ── Search + filter ── */}
        <div style={{
          background: C_BG_CARD,
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          border: `1px solid ${C_BORDER}`,
          borderRadius: 16,
          padding: 20,
          marginBottom: 20,
        }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, position: 'relative', minWidth: 260 }}>
              <input
                type="text"
                placeholder="Search by name, phone, or customer number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ ...inputStyle, paddingLeft: 44 }}
                onFocus={focusInputCyan}
                onBlur={blurInputDefault}
              />
              <span style={{
                position: 'absolute', left: 16, top: '50%',
                transform: 'translateY(-50%)',
                color: C_TEXT_3, fontSize: 16, pointerEvents: 'none',
              }}>🔍</span>
            </div>
            <select
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              className="dark-select"
              style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}
            >
              <option value="all">All Customers</option>
              <option value="account">⭐ Account Customers</option>
              <option value="regular">Regular Customers</option>
            </select>
            <button
              onClick={openNew}
              style={gradientButton}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 8px 30px ${C_CYAN}80`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 4px 20px ${C_CYAN}4D`;
              }}
            >
              + New Customer
            </button>
          </div>
          <p style={{ color: C_TEXT_2, fontSize: 13, marginTop: 12, marginBottom: 0 }}>
            Showing <span style={{ color: C_CYAN_BRIGHT, fontWeight: 700 }}>{filteredCustomers.length}</span> of {customers.length} customers
          </p>
        </div>

        {/* ── List ── */}
        {loading ? (
          <div style={{
            background: C_BG_CARD,
            backdropFilter: 'none',
            border: `1px dashed ${C_BORDER}`,
            borderRadius: 16,
            padding: '60px 20px',
            textAlign: 'center',
          }}>
            <p style={{ color: C_TEXT_3, fontSize: 16, margin: 0 }}>Loading customers…</p>
          </div>
        ) : !hasActiveQuery ? (
          <div style={{
            background: C_BG_CARD,
            backdropFilter: 'none',
            border: `1px dashed ${C_BORDER}`,
            borderRadius: 16,
            padding: '60px 20px',
            textAlign: 'center',
          }}>
            <p style={{ color: C_TEXT_3, fontSize: 16, marginBottom: 8 }}>
              Search by name, phone, or customer number to find a customer.
            </p>
            <p style={{ color: C_TEXT_3, fontSize: 13, margin: 0 }}>
              {customers.length.toLocaleString()} total customers
            </p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div style={{
            background: C_BG_CARD,
            backdropFilter: 'none',
            border: `1px dashed ${C_BORDER}`,
            borderRadius: 16,
            padding: '60px 20px',
            textAlign: 'center',
          }}>
            <p style={{ color: C_TEXT_3, fontSize: 16, marginBottom: 12 }}>No customers found</p>
            <p style={{ color: C_TEXT_3, fontSize: 13, margin: 0 }}>
              Try a different search term or add a new customer
            </p>
          </div>
        ) : (
          <div>
            {visibleCustomers.map(customer => (
              <div
                key={customer.id}
                className="customer-row"
                onClick={() => navigate(`/customer-profile/${customer.id}`)}
                style={{
                  background: C_BG_CARD,
                  border: `1px solid ${C_BORDER}`,
                  borderRadius: 14,
                  padding: '18px 24px',
                  marginBottom: 10,
                  cursor: 'pointer',
                  transition: 'transform 0.3s, border-color 0.3s, box-shadow 0.3s, background 0.3s',
                }}
              >
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 16, flexWrap: 'wrap',
                }}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                      <p style={{ fontWeight: 700, color: C_TEXT, fontSize: 15, margin: 0 }}>
                        {customer.name}
                      </p>
                      {/* Visible in the list, before anyone opens the customer. */}
                      <CreditHoldWarning customer={customer} size="compact" />
                      {customer.isAccountCustomer && (
                        <span style={{
                          background: '#E6F1FB',
                          color: '#0077B6',
                          padding: '2px 10px',
                          borderRadius: 6,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                        }}>
                          ACCOUNT
                        </span>
                      )}
                      {customer.taxExempt && (
                        <span style={{
                          background: '#E6F7EE',
                          color: '#0F6E56',
                          padding: '2px 10px',
                          borderRadius: 6,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                        }}>
                          TAX EXEMPT
                        </span>
                      )}
                    </div>
                    <div style={{
                      display: 'flex', gap: 20, fontSize: 12, color: C_TEXT_2,
                      flexWrap: 'wrap',
                    }}>
                      <span>📞 {getPrimaryPhone(customer) || 'No phone'}</span>
                      <span>📧 {customer.email || 'No email'}</span>
                      <span style={{ color: C_VIOLET_BRIGHT, fontFamily: 'monospace', fontWeight: 700 }}>
                        {customer.customerNumber}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleEdit(customer)}
                      style={cyanGlassButton}
                      onMouseEnter={e => { e.currentTarget.style.background = `${C_CYAN}2E`; }}
                      onMouseLeave={e => { e.currentTarget.style.background = `${C_CYAN}1A`; }}
                    >
                      ✏️ Edit
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => initiateDelete(customer)}
                        style={{
                          background: '#FBECEC',
                          border: '1px solid #A32D2D',
                          color: '#A32D2D',
                          padding: '10px 18px',
                          borderRadius: '10px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'background 0.3s, border-color 0.3s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#F7DADA'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#FBECEC'; }}
                      >
                        🗑 Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Showing X (rendered) of Y (full filtered count) */}
            <p style={{ color: C_TEXT_2, fontSize: 13, marginTop: 16, marginBottom: 0, textAlign: 'center' }}>
              Showing <span style={{ color: C_CYAN_BRIGHT, fontWeight: 700 }}>{visibleCustomers.length}</span> of {filteredCustomers.length}
            </p>

            {visibleCount < filteredCustomers.length && (
              <button
                onClick={() => setVisibleCount(c => c + 50)}
                style={{ ...neutralButton, width: '100%', marginTop: 12 }}
                onMouseEnter={e => { e.currentTarget.style.background = neutralButtonHover; }}
                onMouseLeave={e => { e.currentTarget.style.background = neutralButtonBg; }}
              >
                ↓ Load More
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Modal: New / Edit Customer ── */}
      {showForm && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(3, 4, 94, 0.35)',
            display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            zIndex: 50,
            padding: 20,
          }}
          onClick={closeForm}
        >
          <div
            style={{
              background: C_BG_CARD_SOLID,
              border: `1px solid ${C_BORDER}`,
              borderRadius: 20,
              padding: 32,
              width: '100%',
              maxWidth: 560,
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 50px rgba(3,4,94,0.18)',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{
              fontSize: 22, fontWeight: 800, color: C_TEXT,
              marginTop: 0, marginBottom: 24,
            }}>
              {editingCustomer ? '✏️ Edit Customer' : '+ New Customer'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                <div>
                  <label style={labelStyle}>Full Name <span style={{ color: C_RED_BRIGHT }}>*</span></label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    style={inputStyle}
                    onFocus={focusInputCyan}
                    onBlur={blurInputDefault}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Phone</label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    style={inputStyle}
                    onFocus={focusInputCyan}
                    onBlur={blurInputDefault}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    style={inputStyle}
                    onFocus={focusInputCyan}
                    onBlur={blurInputDefault}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Address</label>
                  <input
                    type="text"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    style={inputStyle}
                    onFocus={focusInputCyan}
                    onBlur={blurInputDefault}
                  />
                </div>
              </div>

              <div style={{ marginTop: 16 }}>
                <label style={labelStyle}>Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                  onFocus={focusInputCyan}
                  onBlur={blurInputDefault}
                />
              </div>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 16px',
                background: '#CDE7F5',
                border: `1px solid ${C_BORDER}`,
                borderRadius: 10,
                cursor: 'pointer',
                marginTop: 16,
              }}>
                <input
                  type="checkbox"
                  checked={form.taxExempt}
                  onChange={(e) => setForm({ ...form, taxExempt: e.target.checked })}
                  style={{ accentColor: C_CYAN, width: 18, height: 18 }}
                />
                <span style={{ color: C_TEXT, fontSize: 14, fontWeight: 600 }}>
                  Tax Exempt Customer
                </span>
              </label>

              <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <button
                  type="submit"
                  style={{ ...gradientButton, flex: 1 }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 8px 30px ${C_CYAN}80`;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 4px 20px ${C_CYAN}4D`;
                  }}
                >
                  💾 {editingCustomer ? 'Save Changes' : 'Create Customer'}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  style={{ ...neutralButton, flex: 1 }}
                  onMouseEnter={e => { e.currentTarget.style.background = neutralButtonHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = neutralButtonBg; }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Delete Customer (admin + password gated) ── */}
      {showDeleteModal && customerToDelete && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(3,4,94,0.5)' }}>
          <div className="rounded-xl p-6 max-w-md w-full" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', boxShadow: '0 8px 24px rgba(3,4,94,0.15)' }}>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#A32D2D' }}>⚠️ Delete Customer</h3>
            <p className="mb-4" style={{ color: '#6B7C93' }}>
              Delete customer <strong>{customerToDelete.name}</strong>? This cannot be undone.
              Deletion is blocked if the customer has any invoices, finalized quotes, or payments.
            </p>
            <p className="font-semibold mb-2" style={{ color: '#03045E' }}>Enter your password to confirm:</p>
            <input
              type="password"
              value={deletePassword}
              onChange={e => setDeletePassword(e.target.value)}
              placeholder="Your login password"
              className="w-full rounded-lg px-4 py-2 mb-2 focus:outline-none"
              style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
              onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
              onKeyDown={e => { if (e.key === 'Enter' && deletePassword && !deleting) confirmDelete(); }}
              autoFocus
            />
            {deleteError && <p className="text-sm mb-2" style={{ color: '#A32D2D' }}>{deleteError}</p>}
            <div className="flex gap-3 mt-4">
              <button
                onClick={confirmDelete}
                disabled={deleting || !deletePassword}
                className="disabled:opacity-50 font-bold px-6 py-2 rounded-lg flex-1 transition"
                style={{ backgroundColor: '#A32D2D', color: '#FFFFFF' }}
              >
                {deleting ? 'Deleting…' : 'Delete Permanently'}
              </button>
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                className="font-bold px-6 py-2 rounded-lg flex-1 transition"
                style={{ backgroundColor: '#E6F1FB', color: '#6B7C93' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}

export default Customers;
